// Shared category icons + colors used across pins, chips, list, and modal.
// SVG paths are hard-coded static content — no user data flows into these.
import React from "react";

// Each category's icon rendered as a React <svg>. Paths are lucide-style, 24x24 viewBox.
const CATEGORY_ICONS = {
    restaurant: (
        <>
            <path d="M3 2v7c0 1.1.9 2 2 2h4V2" />
            <path d="M7 2v20" />
            <path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
        </>
    ),
    retail: (
        <>
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
            <path d="M3 6h18" />
            <path d="M16 10a4 4 0 0 1-8 0" />
        </>
    ),
    warehouse: (
        <>
            <path d="M22 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 4 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 22 16Z" />
            <path d="m3.3 7 8.7 5 8.7-5" />
            <path d="M12 22V12" />
        </>
    ),
    delivery: (
        <>
            <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
            <path d="M15 18H9" />
            <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" />
            <circle cx="17" cy="18" r="2" />
            <circle cx="7" cy="18" r="2" />
        </>
    ),
    hotels: (
        <>
            <path d="M2 4v16" />
            <path d="M2 8h18a2 2 0 0 1 2 2v10" />
            <path d="M2 17h20" />
            <path d="M6 8v9" />
        </>
    ),
    care: (
        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
    ),
    cleaning: (
        <>
            <path d="M14 4l6 6" />
            <path d="M4 20l6-6" />
            <path d="M9.5 8.5 15 3l6 6-5.5 5.5" />
            <path d="m4 20 2 2 4-4-2-2Z" />
        </>
    ),
    security: (
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
    ),
    events: (
        <>
            <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
            <path d="M13 5v2" />
            <path d="M13 17v2" />
            <path d="M13 11v2" />
        </>
    ),
    _default: (
        <>
            <circle cx="12" cy="10" r="3" />
            <path d="M12 22s-8-7.5-8-13a8 8 0 1 1 16 0c0 5.5-8 13-8 13Z" />
        </>
    ),
};

// Raw path strings — needed for the map-marker SVG data-URI (Google Maps).
// Kept in sync with the JSX components above.
export const CATEGORY_PATHS = {
    restaurant:
        '<path d="M3 2v7c0 1.1.9 2 2 2h4V2"/><path d="M7 2v20"/><path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>',
    retail:
        '<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>',
    warehouse:
        '<path d="M22 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 4 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 22 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>',
    delivery:
        '<path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/>',
    hotels:
        '<path d="M2 4v16"/><path d="M2 8h18a2 2 0 0 1 2 2v10"/><path d="M2 17h20"/><path d="M6 8v9"/>',
    care:
        '<path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>',
    cleaning:
        '<path d="M14 4l6 6"/><path d="M4 20l6-6"/><path d="M9.5 8.5 15 3l6 6-5.5 5.5"/><path d="m4 20 2 2 4-4-2-2Z"/>',
    security:
        '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/>',
    events:
        '<path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M13 5v2"/><path d="M13 17v2"/><path d="M13 11v2"/>',
    _default: '<circle cx="12" cy="10" r="3"/><path d="M12 22s-8-7.5-8-13a8 8 0 1 1 16 0c0 5.5-8 13-8 13Z"/>',
};

export const CATEGORY_STROKE = {
    restaurant: "#d94b08",
    retail: "#c98218",
    warehouse: "#4a7c2a",
    delivery: "#1e6bb8",
    hotels: "#a13d94",
    care: "#3958a8",
    cleaning: "#4a536f",
    security: "#a13a3a",
    events: "#b8892e",
    _default: "#e85812",
};

export function CategoryIcon({ catId, size = 18, stroke, className, title }) {
    const inner = CATEGORY_ICONS[catId] || CATEGORY_ICONS._default;
    const strokeColor = stroke || CATEGORY_STROKE[catId] || CATEGORY_STROKE._default;
    return (
        <svg
            className={className}
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={strokeColor}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            role={title ? "img" : "presentation"}
            aria-hidden={title ? undefined : "true"}
            aria-label={title}
            style={{ display: "inline-block", verticalAlign: "middle" }}
        >
            {title ? <title>{title}</title> : null}
            {inner}
        </svg>
    );
}
